import { useState } from "react";

function calcMetrics(trades) {
  if (!trades || trades.length === 0) return null;
  const wins = trades.filter(t => t.pnl > 0);
  const losses = trades.filter(t => t.pnl < 0);
  const totalPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const winRate = trades.length ? Math.round((wins.length / trades.length) * 100) : 0;
  const avgWin = wins.length ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
  const avgLoss = losses.length ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0;

  const emotionStats = {};
  trades.forEach(t => {
    const e = t.emotion || 'sin datos';
    if (!emotionStats[e]) emotionStats[e] = { pnl: 0, count: 0 };
    emotionStats[e].pnl += t.pnl || 0;
    emotionStats[e].count++;
  });

  const setupStats = {};
  trades.forEach(t => {
    const s = t.setup || 'sin datos';
    if (!setupStats[s]) setupStats[s] = { pnl: 0, count: 0 };
    setupStats[s].pnl += t.pnl || 0;
    setupStats[s].count++;
  });

  const pairStats = {};
  trades.forEach(t => {
    const p = t.pair || 'sin datos';
    if (!pairStats[p]) pairStats[p] = { pnl: 0, count: 0 };
    pairStats[p].pnl += t.pnl || 0;
    pairStats[p].count++;
  });

  return {
    totalPnl, winRate, avgWin, avgLoss,
    total: trades.length, wins: wins.length, losses: losses.length,
    emotionStats, setupStats, pairStats
  };
}

export default function AIAnalysis({ trades, session, accounts }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState('all');

  const filteredTrades = () => {
    if (!trades || trades.length === 0) return [];
    const now = Date.now();
    let result = trades;
    if (selectedAccount !== 'all') result = result.filter(t => String(t.account_id) === String(selectedAccount));
    if (period === '7d') return result.filter(t => now - new Date(t.created_at).getTime() < 7 * 86400000);
    if (period === '30d') return result.filter(t => now - new Date(t.created_at).getTime() < 30 * 86400000);
    return result;
  };

  const analyze = async () => {
    const t = filteredTrades();
    setError('');
    setAnalysis(null);

    if (t.length === 0) {
      setError('No tienes trades en este periodo. Registra operaciones en el Journal primero.');
      return;
    }

    // Verificar token de sesión (viene como prop)
    const token = session?.access_token;
    if (!token) {
      setError('Sesión no válida. Cierra sesión y vuelve a entrar.');
      return;
    }

    setLoading(true);

    const m = calcMetrics(t);

    const emotionSummary = Object.entries(m.emotionStats)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([e, s]) => `${e}: ${s.count} trades, PnL $${s.pnl.toFixed(0)}`)
      .join(' | ');

    const setupSummary = Object.entries(m.setupStats)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([s, v]) => `${s}: ${v.count} trades, PnL $${v.pnl.toFixed(0)}`)
      .join(' | ');

    const pairSummary = Object.entries(m.pairStats)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([p, v]) => `${p}: ${v.count} trades, PnL $${v.pnl.toFixed(0)}`)
      .join(' | ');

    const recentTrades = t.slice(0, 20).map((tr, i) =>
      `${i + 1}. ${tr.type || '-'} ${tr.pair || '-'} | PnL: $${(tr.pnl || 0).toFixed(2)} | Emoción: ${tr.emotion || '-'} | Setup: ${tr.setup || '-'} | Session: ${tr.session || '-'} | RR: ${tr.rr || '-'}`
    ).join('\n');

    const prompt = `Eres el analizador de trading de TradeLeash. Analiza el historial de este trader y da un diagnóstico honesto, directo y accionable en español.

RESUMEN ESTADÍSTICO:
- Total trades: ${m.total} | Ganadores: ${m.wins} | Perdedores: ${m.losses}
- Win Rate: ${m.winRate}%
- PnL Total: $${m.totalPnl.toFixed(2)}
- Media ganancia: $${m.avgWin.toFixed(2)} | Media pérdida: $${m.avgLoss.toFixed(2)}
- Ratio ganancias/pérdidas: ${m.avgLoss !== 0 ? Math.abs(m.avgWin / m.avgLoss).toFixed(2) : 'N/A'}

EMOCIONES:
${emotionSummary}

SETUPS:
${setupSummary}

PARES OPERADOS:
${pairSummary}

ÚLTIMOS TRADES (máx 20):
${recentTrades}

Estructura tu respuesta EXACTAMENTE así, sin asteriscos ni markdown:

DIAGNÓSTICO GENERAL
[2-3 frases directas sobre el estado actual de su trading]

PATRÓN EMOCIONAL
[Qué emociones le están costando dinero y por qué. Sé específico con los datos]

SETUP CON MEJOR RENDIMIENTO
[Cuál es su mejor setup y en qué par. Dile que se centre en él]

PUNTO CRÍTICO A CORREGIR
[El problema número uno que tiene que resolver ahora mismo]

ACCIÓN PARA MAÑANA
[Una sola cosa concreta y específica que debe hacer diferente en su próxima sesión]

Sé directo como un mentor experimentado. Sin adulación. Máximo 300 palabras.`;

    try {
      // Llamar al endpoint backend (la API key NO está en el navegador)
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ prompt })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error al analizar');

      setAnalysis({ text: data.text, metrics: m });
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  const m = calcMetrics(filteredTrades());

  const sectionColor = {
    'DIAGNÓSTICO GENERAL': '#059669',
    'PATRÓN EMOCIONAL': '#7c3aed',
    'SETUP CON MEJOR RENDIMIENTO': '#0284c7',
    'PUNTO CRÍTICO A CORREGIR': '#dc2626',
    'ACCIÓN PARA MAÑANA': '#d97706',
  };

  const renderAnalysis = (text) => {
    const sections = text.split('\n\n').filter(s => s.trim());
    return sections.map((section, i) => {
      const lines = section.split('\n');
      const title = lines[0].trim();
      const body = lines.slice(1).join('\n').trim();
      const color = sectionColor[title] || 'var(--ac)';
      return (
        <div key={i} style={{
          background: 'var(--s2)', border: '1px solid var(--b1)',
          borderLeft: `3px solid ${color}`,
          borderRadius: 10, padding: '14px 16px', marginBottom: 10
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color, fontFamily: 'var(--mono)', marginBottom: 6 }}>{title}</div>
          <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--t2)' }}>{body}</div>
        </div>
      );
    });
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>🧠</span>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--t1)' }}>Análisis IA</div>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
            background: 'var(--ac-glow)', color: 'var(--ac)',
            border: '1px solid var(--ac)', borderRadius: 20,
            padding: '2px 8px', textTransform: 'uppercase'
          }}>BETA</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>
          Claude analiza tu historial y detecta patrones, errores y oportunidades de mejora
        </div>
      </div>

      <div style={{
        background: 'var(--s1)', border: '1px solid var(--b1)',
        borderRadius: 12, padding: 20, marginBottom: 16
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Cuenta</div>
          <select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 8, color: 'var(--t1)', fontSize: 13, padding: '7px 12px', outline: 'none', fontFamily: 'var(--sans)', width: '100%' }}>
            <option value='all'>Todas las cuentas</option>
            {accounts && accounts.map(a => <option key={a.id} value={a.id}>{a.name || `Cuenta ${a.id}`}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Periodo a analizar</div>
          {[['all', 'Todos los trades'], ['30d', 'Últimos 30 días'], ['7d', 'Última semana']].map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 7,
              border: `1px solid ${period === v ? 'var(--ac)' : 'var(--b1)'}`,
              background: period === v ? 'var(--ac-glow)' : 'transparent',
              color: period === v ? 'var(--ac)' : 'var(--t3)',
              cursor: 'pointer', fontWeight: period === v ? 600 : 400,
              fontFamily: 'var(--sans)', transition: 'all .15s'
            }}>{l}</button>
          ))}
        </div>

        {m && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Trades', val: m.total, color: 'var(--t1)' },
              { label: 'Win Rate', val: `${m.winRate}%`, color: m.winRate >= 50 ? 'var(--green)' : 'var(--red)' },
              { label: 'PnL Total', val: `${m.totalPnl >= 0 ? '+' : ''}$${m.totalPnl.toFixed(0)}`, color: m.totalPnl >= 0 ? 'var(--green)' : 'var(--red)' },
              { label: 'Avg Win', val: `+$${m.avgWin.toFixed(0)}`, color: 'var(--green)' },
              { label: 'Avg Loss', val: `$${m.avgLoss.toFixed(0)}`, color: 'var(--red)' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700, color }}>{val}</div>
                <div style={{ fontSize: 10, color: 'var(--t4)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={analyze}
          disabled={loading}
          style={{
            background: loading ? 'var(--s3)' : 'var(--ac)',
            color: loading ? 'var(--t3)' : '#fff',
            border: 'none', borderRadius: 9,
            padding: '11px 24px', fontSize: 14, fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--sans)', transition: 'all .15s',
            display: 'flex', alignItems: 'center', gap: 8
          }}
        >
          {loading ? (
            <>
              <span style={{
                width: 14, height: 14, border: '2px solid var(--b2)',
                borderTopColor: 'var(--ac)', borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin .7s linear infinite'
              }} />
              Analizando...
            </>
          ) : '🧠 Analizar mi trading'}
        </button>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {error && (
          <div style={{
            marginTop: 12, background: 'var(--red-bg)', border: '1px solid rgba(220,38,38,0.2)',
            borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', fontFamily: 'var(--mono)'
          }}>{error}</div>
        )}
      </div>

      {analysis && (
        <div style={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
              background: 'var(--ac-glow)', color: 'var(--ac)',
              border: '1px solid var(--ac)', borderRadius: 20,
              padding: '3px 10px', textTransform: 'uppercase'
            }}>Diagnóstico generado</span>
            <span style={{ fontSize: 12, color: 'var(--t4)', fontFamily: 'var(--mono)' }}>
              basado en {analysis.metrics.total} trades
            </span>
          </div>
          {renderAnalysis(analysis.text)}
          <div style={{ marginTop: 14, fontSize: 11, color: 'var(--t4)', fontFamily: 'var(--mono)' }}>
            * Este análisis es orientativo. Las decisiones de trading son tu responsabilidad.
          </div>
        </div>
      )}
    </div>
  );
}
